<template>
  <form
    class="main-cart critical-component-clear container"
    action="/cart"
    method="post"
  >
    <div class="main-cart__header">
      <p
        class="text-heading-m-desktop text-heading-s main-cart__header--title"
        v-text="$string('cart.title')"
      />
      <p
        class="text-heading-3xl-desktop text-heading-2xl main-cart__header--subtitle"
        v-text="$string('cart.subtitle')"
      />
      <div
        v-if="cart.item_count === 0"
        class="main-cart__header--description"
      >
        <p
          class="text-body-m-desktop text-body-s"
          v-text="$string('cart.empty.title')"
        />
        <p
          class="text-body-m-desktop text-body-s"
          v-text="$string('cart.empty.text')"
        />
        <btn
          class="main-cart__header--button"
          :label="$string('cart.continue_shopping')"
          :url="$variable('routes.root')"
        />
      </div>
    </div>

    <div
      v-if="cart.item_count > 0 || cartStatus.label === 'adding'"
      class="main-cart__container grid"
    >
      <div
        class="main-main-cart__items col xs12 l12"
        role="table"
        :aria-label="$string('cart.title')"
      >
        <progress-bar
          v-if="!emptyCart && progressBarDisplay"
          :percentage="progressBarValue"
          :progress-text="progressBarText"
        />
        <div
          class="main-cart__items--table header"
          role="rowgroup"
        >
          <p
            class="main-cart__items--row text-utility-capitalized-s-bold"
            role="columnheader"
            v-text="$string('cart.line_item.item')"
          />
          <p
            class="main-cart__items--row text-utility-capitalized-s-bold"
            role="columnheader"
            v-text="$string('cart.line_item.quantity')"
          />
          <p
            class="main-cart__items--row text-utility-capitalized-s-bold"
            role="columnheader"
            v-text="$string('cart.line_item.price')"
          />
          <div
            class="main-cart__items--row"
            role="columnheader"
          />
        </div>

        <line-item
          v-for="(item) of cartItems"
          :key="getCartItemKey(item)"
          block-class="main-cart"
          :item="item"
        />

        <free-gift />
      </div>

      <aside
        class="main-cart__sidebar col xs12 l15-24"
        :class="{ 'has-progressbar': progressBarDisplay }"
      >
        <cart-summary
          class="main-cart__summary"
          :gift-wrap-product="giftWrapProduct"
          :helpful-links="helpfulLinks"
        />
      </aside>
    </div>
  </form>
</template>

<script>

/**
 * Vue: Main cart (main-cart)
 * -----------------------------------------------------------------------------
 * Cart page with line items.
 * @param {Array} helpfulLinks - Helpful links.
 * @param {Boolean} progressBarDisplay - Shows progress bar.
 *
 */
import { mapActions, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import CartSummary from '~async/main-cart/cart-summary'
import ProgressBar from '~async/progress-bar/progress-bar'
import LineItem from '~async/line-item/line-item'
import FreeGift from '~async/free-gift/free-gift'

export default {
  name: 'MainCart',

  components: {
    Btn,
    CartSummary,
    LineItem,
    ProgressBar,
    FreeGift,
  },

  props: {
    helpfulLinks: {
      type: Array,
      default: () => [],
    },
    progressBarDisplay: {
      type: Boolean,
      default: true,
    },
    giftWrapProduct: {
      type: Object,
      default: () => ({}),
    },
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      cartStatus: (state) => state.cart.status,
    }),

    /**
     * Compute cart items.
     * - For each item that is being added, check if it's already in the cart.
     * - Check against key (if adding from cart) or id (if adding from other).
     * - If it isn't then add a false item to act as a loading placeholder.
     * @returns {Array}
     */
    cartItems() {
      if (this.cartStatus.label !== 'adding') {
        return this.cart.items
      }

      const placeholders = []

      this.cartStatus.items.forEach((addedItem) => {
        const match = this.cart.items.find((cartItem) => {
          const cartItemProperty = typeof addedItem === 'string'
            ? cartItem.key
            : cartItem.id

          return cartItemProperty === addedItem
        })

        if (match) {
          return
        }

        placeholders.push(false)
      })

      return [
        ...placeholders,
        ...this.cart.items,
      ]
    },

    /**
     * Is cart empty.
     * @returns {boolean}
     */
    emptyCart() {
      return this.cart.item_count < 1
    },

    /**
     * Is free shipping threshold met.
     * @returns {boolean}
     */
    cartThresholdMet() {
      return this.cart?.total_price >= this.$variable('settings.freeShipping.threshold')
    },

    /**
     * Gets the remaining number for the free shipping threshold.
     * @returns {number}
     */
    freeDeliveryRemainingValue() {
      return this.$variable('settings.freeShipping.threshold') - this.cart?.total_price
    },

    /**
     * Gets the percentage value to meet free shipping threshold.
     * @returns {number}
     */
    progressBarValue() {
      if (this.cartThresholdMet) {
        return 100
      }
      // eslint-disable-next-line no-mixed-operators
      return 100 - ((
        this.$variable('settings.freeShipping.threshold') - this.cart?.total_price
        // eslint-disable-next-line no-mixed-operators
      ) * 100) / this.$variable('settings.freeShipping.threshold')
    },

    /**
     * Decide which text will be shown on free shipping progress bar.
     * @returns {string}
     */
    progressBarText() {
      return this.cartThresholdMet
        ? this.$string('cart.free_delivery')
        : this.$string('cart.spend_more', {
          replace: {
            value: this.$formatMoney(this.freeDeliveryRemainingValue),
          },
        })
    },

    /**
     * Decide which text will be shown on free shipping progress bar.
     * @returns {string}
     */
    isJustGiftWrapProduct() {
      return this.cartItems.length === 1 && this.cartItems[0]?.tags.some(tag => tag.includes('Collection: Gift wrap'))
    },
  },

  watch: {
    isJustGiftWrapProduct() {
      if (this.isJustGiftWrapProduct) {
        this.removeItem(this.cartItems[0]?.key)
      }
    },
  },

  mounted() {
    if (this.isJustGiftWrapProduct) {
      this.removeItem(this.cartItems[0]?.key)
    }
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      removeItem: 'cart/updateItem',
    }),

    /**
     * Get cart item key.
     * @param {Object} item - Line item.
     * @returns {String}
     */
    getCartItemKey(item) {
      if (!item) {
        return 'cart-line-item-loading'
      }

      return `cart-line-item-${item.key}`
    },
  },
}
</script>

<style lang="scss">
@import './main-cart';
</style>
